import type {ParsedUrlQuery} from 'node:querystring';
import {destroyCookie, parseCookies, setCookie} from 'nookies';

// プロモーションキャンペーンコード(pcid)を保持するファーストパーティCookieのキー。
// 任意ページの ?pcid= 来訪から会員登録完了までを計測するため、サイト全域で捕捉する。
// JSから読む必要があるためHttpOnlyは付与できない。
export const PROMOTION_CAMPAIGN_CODE_COOKIE_KEY = 'gsskt_pcid';

// Cookieの有効期限は30日
export const PROMOTION_CAMPAIGN_CODE_COOKIE_MAX_AGE = 60 * 60 * 24 * 30;

// 正規のコードはadminが自動発行する英数小文字6文字(DB上はvarchar(50))。
// pcidはユーザーが自由に書き換えられるURLクエリ由来のため、巨大な値や不正な文字を
// Cookieに保存しない(Cookie肥大化によるリクエストヘッダ超過を防ぐ)よう形式で検証する。
const PROMOTION_CAMPAIGN_CODE_PATTERN = /^[0-9A-Za-z_-]{1,50}$/;

export const isValidPromotionCampaignCode = (code: string): boolean =>
  PROMOTION_CAMPAIGN_CODE_PATTERN.test(code);

export const extractPromotionCampaignCodeFromQuery = (
  query: ParsedUrlQuery,
): string | undefined => {
  const {pcid} = query;
  if (pcid === undefined) {
    return undefined;
  }
  // 同名クエリが複数付与された場合は配列になるため先頭を採用する
  const code = Array.isArray(pcid) ? pcid[0] : pcid;
  return code !== undefined && isValidPromotionCampaignCode(code)
    ? code
    : undefined;
};

export const savePromotionCampaignCode = (code: string): void => {
  setCookie(null, PROMOTION_CAMPAIGN_CODE_COOKIE_KEY, code, {
    maxAge: PROMOTION_CAMPAIGN_CODE_COOKIE_MAX_AGE,
    path: '/',
    sameSite: 'lax',
  });
};

// 来訪URLに pcid があれば Cookie に保存する。後から別の pcid を踏んだら上書き(last-touch)。
export const capturePromotionCampaignCode = (query: ParsedUrlQuery): void => {
  const code = extractPromotionCampaignCodeFromQuery(query);
  if (code === undefined) {
    return;
  }
  savePromotionCampaignCode(code);
};

// クエリを優先し、無ければ Cookie をフォールバックとして読む。
export const resolvePromotionCampaignCode = (
  query: ParsedUrlQuery,
  cookies?: Partial<Record<string, string>>,
): string | undefined => {
  const codeFromQuery = extractPromotionCampaignCodeFromQuery(query);
  if (codeFromQuery !== undefined) {
    return codeFromQuery;
  }
  const resolvedCookies = cookies ?? parseCookies();
  const codeFromCookie = resolvedCookies[PROMOTION_CAMPAIGN_CODE_COOKIE_KEY];
  // 修正前に保存された不正なCookie値が残っている可能性があるため、読み出し時も検証する
  return codeFromCookie !== undefined &&
    isValidPromotionCampaignCode(codeFromCookie)
    ? codeFromCookie
    : undefined;
};

export const clearPromotionCampaignCode = (): void => {
  destroyCookie(null, PROMOTION_CAMPAIGN_CODE_COOKIE_KEY, {path: '/'});
};
